/**
 * Factory de décorateurs pour différer l'exécution d'une méthode.
 * @internal
 */
function createAsyncDecorator(
  scheduler: (callback: () => void) => void,
  name: string,
) {
  return function (target: Function, context: ClassMethodDecoratorContext) {
    const methodName = String(context.name);

    return function (this: any, ...args: any[]) {
      // Planifie l'exécution
      scheduler(() => {
        try {
          // Exécute la méthode originale avec le contexte et les arguments préservés
          target.apply(this, args);
        } catch (error) {
          console.error(
            `[${name}] Error executing deferred method '${methodName}'`,
            error,
          );
        }
      });
    };
  };
}

/**
 * Diffère l'exécution de la méthode à la fin de la boucle d'événements courante (Macrotask).
 * Utilise `setTimeout(..., delay)`.
 *
 * Idéal pour attendre que le parser HTML ait fini de construire le DOM (ex: Web Components).
 *
 * @param delay Délai en millisecondes (défaut: 0).
 */
export function Defer(delay: number = 0) {
  return function (target: Function, context: ClassMethodDecoratorContext) {
    return createAsyncDecorator((fn) => setTimeout(fn, delay), 'Defer')(
      target,
      context,
    );
  };
}

/**
 * Diffère l'exécution de la méthode à la fin de la stack courante, mais AVANT le prochain rendu (Microtask).
 * Utilise `queueMicrotask(...)`.
 *
 * Idéal pour des mises à jour d'état qui ne doivent pas bloquer le thread courant
 * mais doivent se produire avant que le navigateur ne peigne l'écran (évite les glitchs visuels).
 */
export function Microtask() {
  return function (target: Function, context: ClassMethodDecoratorContext) {
    return createAsyncDecorator((fn) => queueMicrotask(fn), 'Microtask')(
      target,
      context,
    );
  };
}

/**
 * Diffère l'exécution de la méthode juste avant le prochain rafraîchissement de l'écran (Paint).
 * Utilise `requestAnimationFrame(...)`.
 *
 * Idéal pour les manipulations DOM visuelles ou les animations afin de garantir la fluidité (60fps)
 * et éviter le "Layout Thrashing".
 */
export function RenderFrame() {
  return function (target: Function, context: ClassMethodDecoratorContext) {
    return createAsyncDecorator(
      (fn) => requestAnimationFrame(fn),
      'RenderFrame',
    )(target, context);
  };
}
